/**
 * The Sales tab's sub-menu, and which permission each sheet behind it needs.
 *
 * WHY THIS FILE EXISTS
 *
 * Committed, Cash and Cancelled are `anyAuthenticated` server-side. The two
 * credit sheets are not: `/getcredit` and `/getcreditcleared` both demand
 * credit.view, which the default Inventory Manager role does not hold. Both
 * tabs rendered for everyone anyway, and — worse — CarsNav landed the Sales tab
 * on Credit, so that role's very first click on Sales fetched a sheet the
 * server refused and answered with "Couldn't load Credit: Access denied:
 * requires credit.view permission". A tab whose only possible outcome is an
 * error is not a tab, and a permission the user was never granted is not news.
 *
 * The list lives here rather than in SubNav.jsx because CarsNav owns the
 * sub-menu state and has to know where Sales may land. Two files each keeping
 * their own idea of a tab's permission is the exact failure tabPermissions.js
 * was extracted to prevent; this is the same hazard one level down.
 *
 * Kept dependency-free on purpose, like settingsSections.js and
 * tabPermissions.js: SubNav pulls in every sheet component in the app, so a
 * test of these three functions would otherwise mount half of it.
 *
 * `perms` is any-of, matching `can(...perms)` in permissions.js. No `perms` at
 * all means everyone, and declaration order is strip order.
 */
export const SALES_SUBTABS = Object.freeze([
    Object.freeze({ key: "Committed",     label: "Committed",      id: "sub-menu-committed" }),
    Object.freeze({ key: "Cash",          label: "Cash",           id: "sub-menu-cash" }),
    Object.freeze({ key: "Credit",        label: "Credit",         id: "sub-menu-credit",         perms: Object.freeze(["credit.view"]) }),
    Object.freeze({ key: "CreditCleared", label: "Credit Cleared", id: "sub-menu-credit-cleared", perms: Object.freeze(["credit.view"]) }),
    Object.freeze({ key: "Cancelled",     label: "Cancelled",      id: "sub-menu-cancelled" }),
]);

/** The Sales sub-tabs this user may open, in strip order. */
export function salesSubTabs(can) {
    return SALES_SUBTABS.filter((t) => !t.perms || can(...t.perms));
}

/**
 * Whether a sub-menu key is one this user may open.
 *
 * Unknown keys pass. The other tabs' sub-menus (Inventory, Customers, History…)
 * are not this file's business, and callers like CarsNav's "navigate" handler
 * see all of them — returning false there would break every non-Sales jump.
 */
export function canSeeSalesSubTab(key, can) {
    const tab = SALES_SUBTABS.find((t) => t.key === key);
    return !tab || !tab.perms || can(...tab.perms);
}

/**
 * Where the Sales tab lands. Credit by preference — that is what the tab is
 * opened for and what it has always done — falling back to the first sheet the
 * user can actually read.
 */
export function defaultSalesSubMenu(can) {
    return canSeeSalesSubTab("Credit", can) ? "Credit" : (salesSubTabs(can)[0]?.key ?? "");
}
